<script lang="ts">
  import { listDemoKeys, createDemoKey, revokeDemoKey, restoreDemoKey, deleteDemoKey, demoLink, trackerLink, keyState, DEMO_URL, TRACKER_URL, type DemoKeyRow } from '../lib/demoAccess';

  let rows = $state<DemoKeyRow[]>([]);
  let datasets = $state<Record<string, string>>({});
  let loading = $state(true);
  let error = $state('');
  let busyKey = $state('');

  // Create form
  let label = $state('');
  let note = $state('');
  let expiresDays = $state(30);
  let dataset = $state('t2dm-001');
  let creating = $state(false);
  let issued = $state<{ key: string; label: string; expiresAt: number } | null>(null);
  let copied = $state('');

  async function load() {
    loading = true; error = '';
    try { const r = await listDemoKeys(); rows = r.rows; datasets = r.datasets; }
    catch (e) { error = (e as Error)?.message || String(e); rows = []; }
    finally { loading = false; }
  }
  $effect(() => { load(); });

  async function create() {
    if (!label.trim()) return;
    creating = true; error = '';
    try {
      const r = await createDemoKey({ label: label.trim(), note: note.trim() || undefined, expiresDays: expiresDays || undefined, dataset });
      issued = { key: r.key, label: label.trim(), expiresAt: r.expiresAt };
      label = ''; note = '';
      await load();
    } catch (e) { error = (e as Error)?.message || String(e); }
    finally { creating = false; }
  }
  async function act(kind: 'revoke' | 'restore' | 'delete', r: DemoKeyRow) {
    if (kind === 'revoke' && !confirm(`Revoke ${r.key} (${r.label})? The demo stops opening with it immediately.`)) return;
    if (kind === 'delete' && !confirm(`Delete ${r.key} (${r.label}) permanently? Its usage history goes with it.`)) return;
    busyKey = r.key;
    try { await (kind === 'revoke' ? revokeDemoKey : kind === 'restore' ? restoreDemoKey : deleteDemoKey)(r.key); await load(); }
    catch (e) { alert(`${kind} failed: ${(e as Error)?.message ?? e}`); }
    finally { busyKey = ''; }
  }
  async function copy(text: string, tag: string) {
    try { await navigator.clipboard.writeText(text); copied = tag; setTimeout(() => (copied = ''), 1600); } catch { prompt('Copy:', text); }
  }
  const fmt = (ms: number) => (ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
  const fmtWhen = (ms: number) => { if (!ms) return 'never'; const s = Math.floor((Date.now() - ms) / 1000); return s < 60 ? 'just now' : s < 3600 ? `${Math.floor(s / 60)}m ago` : s < 86400 ? `${Math.floor(s / 3600)}h ago` : s < 604800 ? `${Math.floor(s / 86400)}d ago` : fmt(ms); };
  const counts = $derived({ active: rows.filter((r) => keyState(r) === 'active').length, used: rows.filter((r) => r.uses > 0).length });
</script>

<div class="body">
  <div class="head">
    <div>
      <h1>Demo access</h1>
      <p class="muted">Access keys for the physician demo. One key per person, no sign-in. It opens the patient app on a synthetic 12-year diabetes record at <a href={DEMO_URL} target="_blank" rel="noopener"><code>{DEMO_URL.replace('https://', '')}</code></a> and the physician tracking view at <a href={TRACKER_URL} target="_blank" rel="noopener"><code>{TRACKER_URL.replace('https://', '')}</code></a>.</p>
    </div>
    <button class="sync" onclick={load} disabled={loading}>{loading ? 'Loading…' : '⟳ Refresh'}</button>
  </div>

  {#if error}<div class="banner err"><strong>Something failed.</strong> {error}</div>{/if}

  <form class="create" onsubmit={(e) => { e.preventDefault(); void create(); }}>
    <input class="in" type="text" placeholder="Who is this key for? (e.g. Dr A. Rao, City Hospital)" bind:value={label} required maxlength="80" />
    <input class="in" type="text" placeholder="Note (optional)" bind:value={note} maxlength="300" />
    <select class="in" bind:value={expiresDays} title="Expiry">
      <option value={7}>Expires in 7 days</option><option value={30}>Expires in 30 days</option><option value={90}>Expires in 90 days</option><option value={0}>Never expires</option>
    </select>
    <select class="in" bind:value={dataset} title="Dataset">
      {#each Object.entries(datasets) as [id, lbl] (id)}<option value={id}>{lbl}</option>{/each}
      {#if !Object.keys(datasets).length}<option value="t2dm-001">Type 2 diabetes record</option>{/if}
    </select>
    <button class="primary" type="submit" disabled={creating || !label.trim()}>{creating ? 'Issuing…' : 'Issue key'}</button>
  </form>

  {#if issued}
    <div class="issued">
      <div class="ititle">Key issued for <b>{issued.label}</b>{#if issued.expiresAt} · expires {fmt(issued.expiresAt)}{/if}</div>
      <div class="irow">
        <span class="mono bigkey">{issued.key}</span>
        <button class="act" onclick={() => copy(issued!.key, 'key')}>{copied === 'key' ? 'Copied' : 'Copy key'}</button>
        <button class="act" onclick={() => copy(demoLink(issued!.key), 'link')}>{copied === 'link' ? 'Copied' : 'Copy app link'}</button>
        <a class="act" href={demoLink(issued.key)} target="_blank" rel="noopener">Open app</a>
        <a class="act" href={trackerLink(issued.key)} target="_blank" rel="noopener">Open tracker</a>
        <button class="act ghost" onclick={() => (issued = null)}>Dismiss</button>
      </div>
      <div class="t-foot">App link <span class="mono">{demoLink(issued.key)}</span> · tracker link <span class="mono">{trackerLink(issued.key)}</span>. Share a link or the key by whatever channel you use with the doctor.</div>
    </div>
  {/if}

  <div class="stats">
    <span class="stat"><b class="num">{rows.length}</b> keys</span>
    <span class="stat ok"><b class="num">{counts.active}</b> active</span>
    <span class="stat"><b class="num">{counts.used}</b> used at least once</span>
  </div>

  <div class="tablewrap">
    <table>
      <thead><tr><th>For</th><th>Key</th><th>Status</th><th>Created</th><th>Expires</th><th>Opens</th><th>Last used</th><th class="ta-r">Actions</th></tr></thead>
      <tbody>
        {#if loading}
          <tr><td colspan="8" class="empty">Loading…</td></tr>
        {:else if rows.length === 0}
          <tr><td colspan="8" class="empty">No keys yet — issue one above.</td></tr>
        {:else}
          {#each rows as r (r.key)}
            {@const st = keyState(r)}
            <tr class:dim={st !== 'active'}>
              <td><span class="lbl">{r.label}</span>{#if r.note}<span class="note">{r.note}</span>{/if}<span class="note">{r.datasetLabel}</span></td>
              <td><span class="mono key">{r.key}</span><button class="copy" onclick={() => copy(demoLink(r.key), r.key)} title="Copy one-click link">{copied === r.key ? 'copied' : 'link'}</button></td>
              <td><span class="pill {st}">{st}</span></td>
              <td>{fmt(r.createdAt)}</td>
              <td>{r.expiresAt ? fmt(r.expiresAt) : 'never'}</td>
              <td><span class="num">{r.uses}</span></td>
              <td><span title={r.lastIp ? `from ${r.lastIp}` : ''}>{fmtWhen(r.lastUsedAt)}</span>{#if r.lastIp}<span class="note mono">{r.lastIp}</span>{/if}</td>
              <td class="ta-r">
                <div class="rowacts">
                  {#if st === 'revoked'}<button class="act" onclick={() => act('restore', r)} disabled={busyKey === r.key}>Restore</button>
                  {:else}<button class="act danger" onclick={() => act('revoke', r)} disabled={busyKey === r.key}>Revoke</button>{/if}
                  <button class="act ghost" onclick={() => act('delete', r)} disabled={busyKey === r.key}>Delete</button>
                </div>
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
  <p class="footnote">Keys are validated server-side on every open (<code>/api/demo/open</code>); the dataset is only returned after a key checks out. <b>Revoke</b> is instant and reversible; <b>Delete</b> removes the key and its usage trail. The record is synthetic — no PHI is involved.</p>
</div>

<style>
  .body { width: 100%; padding: var(--space-6) var(--space-8); display: flex; flex-direction: column; gap: var(--space-4); }
  .head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  .muted { color: var(--text-3); margin-top: 2px; }
  code { font-family: var(--mono); font-size: 0.92em; color: var(--accent); background: var(--accent-soft); padding: 1px 5px; border-radius: 5px; }
  .sync { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .banner { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); font-size: var(--fs-xs); }
  .banner.err { color: var(--text-2); background: var(--surface); border: 1px solid var(--line-strong); }
  .create { display: grid; grid-template-columns: 2fr 2fr auto auto auto; gap: var(--space-2); align-items: center; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3); }
  @media (max-width: 1000px) { .create { grid-template-columns: 1fr 1fr; } }
  .in { padding: var(--space-2) var(--space-3); border: 1px solid var(--line-strong); border-radius: var(--radius-sm); background: var(--surface); }
  .primary { padding: var(--space-2) var(--space-4); border-radius: var(--radius-sm); background: var(--accent-grad); color: var(--on-accent); font-weight: 700; font-size: var(--fs-xs); }
  .primary:disabled { opacity: 0.55; cursor: default; }
  .issued { display: flex; flex-direction: column; gap: var(--space-2); background: var(--ok-soft); border: 1px solid color-mix(in srgb, var(--ok) 40%, transparent); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  .ititle { color: var(--ok); font-weight: 600; }
  .irow { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
  .bigkey { font-size: var(--fs-heading); font-weight: 800; letter-spacing: 0.14em; color: var(--text); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 4px 10px; }
  .stats { display: flex; flex-wrap: wrap; gap: var(--space-2) var(--space-4); }
  .stat { font-size: var(--fs-xs); color: var(--text-3); }
  .stat b { color: var(--text); margin-right: 4px; font-size: var(--fs-heading); }
  .stat.ok b { color: var(--ok); }
  .tablewrap { border: 1px solid var(--line); border-radius: var(--radius-md); overflow-x: auto; background: var(--surface); }
  table { width: 100%; border-collapse: collapse; min-width: 860px; }
  th, td { text-align: left; padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--line); vertical-align: middle; }
  thead th { font-size: var(--fs-2xs); letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-3); background: var(--surface-2); }
  tbody tr:last-child td { border-bottom: none; }
  tr.dim td { color: var(--text-3); }
  .ta-r { text-align: right; }
  .empty { text-align: center; color: var(--text-3); padding: var(--space-8); }
  .mono { font-family: var(--mono); }
  .lbl { display: block; font-weight: 700; }
  .note { display: block; font-size: var(--fs-2xs); color: var(--text-3); }
  .key { font-weight: 700; letter-spacing: 0.08em; color: var(--accent-2); }
  .copy { margin-left: 6px; font-size: var(--fs-2xs); color: var(--text-3); border: 1px solid var(--line-strong); border-radius: var(--radius-pill); padding: 0 6px; }
  .copy:hover { color: var(--accent); border-color: var(--accent); }
  .pill { font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; border-radius: var(--radius-pill); padding: 2px 8px; }
  .pill.active { color: var(--ok); background: var(--ok-soft); }
  .pill.expired { color: var(--warn); background: var(--warn-soft); }
  .pill.revoked { color: var(--danger); background: var(--danger-soft); }
  .rowacts { display: inline-flex; gap: var(--space-2); justify-content: flex-end; }
  .act { font-size: var(--fs-2xs); font-weight: 700; padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); border: 1px solid var(--line-strong); color: var(--text-2); background: var(--surface-2); display: inline-block; }
  .act:hover:not(:disabled) { color: var(--accent); border-color: var(--accent); }
  .act.danger:hover:not(:disabled) { color: var(--danger); border-color: var(--danger); }
  .act.ghost { background: transparent; border-color: transparent; }
  .act:disabled { opacity: 0.5; cursor: default; }
  .footnote { color: var(--text-3); font-size: var(--fs-2xs); line-height: 1.6; }
  .footnote b { color: var(--text-2); }
</style>
